import { useCallback, useEffect, useMemo } from "react";
import { useToggle } from "react-use";
import { Button, Flex, Message, Modal, Space, Switch, Tooltip, Typography } from "@payping/web-components";
import { SubmitHandler, useForm } from "react-hook-form";

import { RHFInputNumber } from "@/shared/components";
import useTranslator from "@/shared/translate/translator";
import type { TransactionReportViewModel } from "@/shared/domain/transactions";
import { useCreateRefund, useGetServiceConfiguration, useGetUserConfiguration } from "@/refund/use-cases";
import { calcDiffDate, panSeparator } from "@/shared/utils";
import { RefundInfo, RefundOperationStateEnum } from "@/refund/repository/refund/v1/refund-v1-api";
import { EXPIRATION_DIRECT_METHOD, MIN_AMOUNT } from "@/refund/constants/general.constant";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

// type Props = {
//   details?: Omit<TransactionReportViewModel, "cardNo" | "ipg"> &
//     Partial<Pick<TransactionReportViewModel, "cardNo" | "ipg">>;
// };
type Props = {
  details?: TransactionReportViewModel;
  refundInfo?: RefundInfo;
  loading: boolean;
};

type CreateModalConfigProp = {
  amount?: number;
  pan?: string;
  name?: string;
};

// type PanValidationProps = {
//   mask: string;
//   value: string;
//   message: string;
// };

interface FormValues {
  amount: number;
  pan?: string;
}

const CreateRefundAction: React.FC<Props> = ({ details, refundInfo, loading }) => {
  const { t: generalT } = useTranslator();
  const { t } = useTranslator("refund.createAction");
  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      amount: details?.amount || 0,
      pan: "",
    },
  });
  const getUserConfiguration = useGetUserConfiguration();
  const amount = watch("amount");
  // const panValidation = (props: PanValidationProps) => {
  //   // return true;
  //   // Extract the first 6 digits and last 4 digits from the mask
  //   const prefix = props.mask.slice(0, 6);
  //   const suffix = props.mask.slice(-4);

  //   // Create a regex to match the first 6 and last 4 digits
  //   const regex = new RegExp(`^${prefix}\\d*${suffix}$`);

  //   // Check if the value matches
  //   return regex.test(props.value) || props.message;
  // };

  const serviceConfiguration = useGetServiceConfiguration();
  const createRefund = useCreateRefund();

  // todo: change fee & minAmount base on new status or data
  const [on, toggle] = useToggle(false);
  const fee = getUserConfiguration.data?.data.wageValue ?? 0;
  const minAmount = MIN_AMOUNT;
  const expirationDirectMethod = EXPIRATION_DIRECT_METHOD;

  // todo: unComment this only if you have been implemented retry-refund-action & cancel-refund-action
  const iRefundEnable = useMemo(() => {
    // loading
    if (serviceConfiguration.isLoading || loading || getUserConfiguration.isLoading) return false;
    // not paid
    if (!details?.isPaid) return t("modal.refundDisabledReason.primaryTransactionFailed") as string;
    // minAmount
    if ((details?.amount ?? -1) < minAmount) return t("modal.refundDisabledReason.minAmount") as string;
    // supported payment version
    if (details?.version !== 2) return t("modal.refundDisabledReason.oldVersionTransaction") as string;
    // have already refund
    if (
      refundInfo?.state &&
      (refundInfo.state === RefundOperationStateEnum.Created ||
        refundInfo.state === RefundOperationStateEnum.PendingBlockage ||
        refundInfo.state === RefundOperationStateEnum.PreparedToProcess ||
        refundInfo.state === RefundOperationStateEnum.ProcessingRefund ||
        refundInfo.state === RefundOperationStateEnum.RefundSucceeded)
    )
      return t("modal.refundDisabledReason.refunded") as string;
    return true;
  }, [details, getUserConfiguration.isLoading, loading, minAmount, refundInfo, serviceConfiguration.isLoading, t]);

  const isDirectRefund = useMemo(() => {
    if (serviceConfiguration.data?.supportedIpgTypes?.length && details?.ipg) {
      const supportedIpgType = serviceConfiguration.data.supportedIpgTypes.includes(details.ipg);
      if (supportedIpgType) {
        const diffInDays = calcDiffDate(details.payDate || "");
        if (diffInDays < expirationDirectMethod) return true;
        return false;
      }
      return false;
    }
    return false;
  }, [serviceConfiguration.data, details?.ipg, details?.payDate]);

  const openModal = () => {
    if (iRefundEnable) toggle();
  };

  const [check, toggleCheck] = useToggle(true);
  const handleChangeCheck = useCallback(
    (checked: boolean) => {
      toggleCheck(checked);
      if (checked && details?.amount && amount !== details?.amount) setValue("amount", details?.amount);
    },
    [toggleCheck, setValue, amount, details?.amount],
  );

  const checkAmount = useCallback(
    (value: number) => {
      if (value) {
        if (check && details?.amount && value < details?.amount) toggleCheck(false);
        if (!check && details?.amount && value >= details?.amount) toggleCheck(true);
      }
    },
    [check, details?.amount, toggleCheck],
  );
  useEffect(() => {
    checkAmount(amount);
  }, [amount, checkAmount]);

  const [modal, contextHolder] = Modal.useModal();
  const createModalConfig = (prop: CreateModalConfigProp) => ({
    title: t("title"),
    content: (
      <Flex className="tw-w-full" wrap>
        <Flex align="center">
          {t("modal.content1", {
            amount: formatLocalizedNumber({ value: prop.amount ?? 0 }),
          })}
          <CuToman width={24} height={24} />
        </Flex>
        <div className="ltr !tw-dir-ltr">{panSeparator(prop.pan || "")}</div>
        <div>{t("modal.content2", { name: prop.name })}</div>
      </Flex>
    ),
    cancelText: generalT("common.cancel"),
    okText: t("modal.okText"),
  });
  const createRefundHandler: SubmitHandler<FormValues> = async (data) => {
    try {
      // const config = createModalConfig({ amount: data.amount, pan: data.pan || details?.cardNo, name: details?.name });
      const config = createModalConfig({ amount: data.amount, pan: data.pan || details?.cardNo, name: details?.name });

      const confirmed = await modal.confirm(config);
      if (confirmed) {
        // await createRefund.mutateAsync({
        //   direct: isDirectRefund,
        //   amount: data.amount,
        //   paymentCode: details?.code,
        //   pan: data.pan,
        // });

        await createRefund.mutateAsync({
          direct: isDirectRefund,
          amount: data.amount,
          paymentCode: details?.code,
        });
        Message.success({ content: t("success"), duration: 2, key: "create_refund" });
        toggle();
      }
    } catch (error) {
      //
    }
  };

  return (
    <>
      {iRefundEnable === true ? (
        <Button type="primary" onClick={openModal}>
          {t("title")}
        </Button>
      ) : (
        <Tooltip placement="top" title={typeof iRefundEnable === "string" ? iRefundEnable : undefined}>
          <Button type="primary" disabled loading={serviceConfiguration.isLoading || loading}>
            {t("title")}
          </Button>
        </Tooltip>
      )}

      <Modal
        open={on}
        onOk={handleSubmit(createRefundHandler)}
        onCancel={toggle}
        title={
          <Space className="tw-font-medium">
            {t("modal.title")}
            <Flex align="center">
              {formatLocalizedNumber({ value: details?.amount ?? 0 })}
              <CuToman width={24} height={24} />
            </Flex>
          </Space>
        }
        footer={[
          <Button
            key="submit"
            type="primary"
            block
            onClick={handleSubmit(createRefundHandler)}
            disabled={createRefund.isPending}
            loading={createRefund.isPending}
          >
            {t("modal.submit")}
          </Button>,
        ]}
      >
        <Flex vertical className="tw-mt-[24px] tw-w-full">
          <Flex justify="space-between" className="tw-mb-[24px]">
            <Typography.Paragraph>{t("modal.check")}</Typography.Paragraph>
            <Switch checked={check} onChange={handleChangeCheck} />
          </Flex>

          <RHFInputNumber
            name="amount"
            control={control}
            rules={{
              required: generalT("common.validations.required", { name: t("modal.amount") }),
              min: {
                value: minAmount,
                message: generalT("common.validations.min", { name: t("modal.amount"), min: minAmount }),
              },
              max: {
                value: details?.amount || minAmount,
                message: generalT("common.validations.max", {
                  name: t("modal.amount"),
                  max: details?.amount,
                }),
              },
            }}
            disabled={createRefund.isPending}
            className="tw-w-full"
            suffix={<CuToman width={24} height={24} />}
            controls={false}
            separateThousandth
            step={1000}
          />

          {/* {!isDirectRefund && (
            <>
              <Divider className="tw-mt-[4px]" />
              <Typography.Text className="tw-mb-[16px] !tw-text-[14px] !tw-font-medium">
                {t("modal.refundToPan")}
              </Typography.Text>
              <Alert className="tw-mb-4" message={t("modal.notAutomatePanAlert")} type="info" />
              <RHFPanInput
                label={`${t("modal.pan")}:`}
                labelClassName="!tw-font-medium"
                name="pan"
                control={control}
                rules={{
                  required: generalT("common.validations.required", { name: t("modal.pan") }),
                  validate: (value: string) =>
                    panValidation({
                      mask: details?.cardNo || "",
                      value,
                      message: generalT("common.validations.pan"),
                    }),
                }}
                disabled={createRefund.isPending}
              />
            </>
          )} */}

          <Flex align="center">
            <Typography.Text className="!tw-text-[14px]">
              {t("modal.fee", { amount: formatLocalizedNumber({ value: fee }) })}
            </Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Flex>
      </Modal>
      {contextHolder}
    </>
  );
  return "";
};
export default CreateRefundAction;
